import { CancelTokenSource } from 'axios';
import { NestedRecord } from './context';

export interface GeneratedFile {
  container_id: string;
  download_url: string;
  file_id: string;
  name: string;
  size: number;
}

export interface ToolCall {
  tool_name: string;
  message: string;
  id?: string;
  mcp_id?: number;
  has_widget?: boolean;
  ui_resource_uri?: string;
  widget_result?: unknown;
  widget_arguments?: unknown;
  mcp_error?: unknown;
  is_loading?: boolean;
}

export interface DownloadFilePayload {
  filename: string;
  container_id: string;
  file_id: string;
  session_id: string;
}

export interface DataItem {
  isStreaming?: boolean;
  thinkingText?: string;
  thinkingDurationSec?: number;
  isThinking?: boolean;
  generationStopped?: boolean;
  role?: string;
  generated_by?: string;
  message?: string | string[];
  type?: string;
  time?: Date | string;
  stream_end?: boolean;
  webSearchUsed?: boolean;
  webSearchQuery?: string;
  routedModelName?: string;
  generatedFiles?: GeneratedFile[];
  toolCalls?: ToolCall[];
  mcpWarnings?: string[];
  sources?: { title: string; link: string; hostname?: string; snippet?: string }[];
  tokenUsage?: {
    input_tokens?: number;
    reasoning_tokens?: number;
    output_tokens?: number;
  };
}

export const getMessageVersions = (message: DataItem['message']): string[] => {
  if (message == null) return [];
  if (Array.isArray(message)) {
    return message.filter((part): part is string => typeof part === 'string');
  }
  return typeof message === 'string' ? [message] : [];
};

export const hasMessageText = (message: DataItem['message']): boolean => {
  if (message == null) return false;
  if (Array.isArray(message)) {
    return message.some((part) => typeof part === 'string' && part.trim().length > 0);
  }
  return message.trim().length > 0;
};

/** Clear in-flight stream flags after reload — no live stream can survive a refresh. */
export const sanitizeChatHistoryForHydration = (chatData: DataItem[]): DataItem[] => {
  return chatData
    .map((msg) => {
      if (msg.role !== 'AI') return msg;
      if (!msg.isThinking && !msg.isStreaming) return msg;

      const hasContent =
        hasMessageText(msg.message) ||
        (typeof msg.thinkingText === 'string' && msg.thinkingText.trim().length > 0) ||
        Boolean(msg.toolCalls && msg.toolCalls.length > 0);

      if (!hasContent) {
        return null;
      }

      return {
        ...msg,
        isThinking: false,
        isStreaming: false,
        stream_end: true,
      };
    })
    .filter((msg): msg is DataItem => msg != null);
};

export interface initialStateProps {
  rag?: boolean | (() => boolean | void) | null | string;
  queryIndex?: number | null;
  responseIndex?: number | null;
  showQueryActions?: boolean;
  showResponseActions?: boolean;
  showEditQueryActions?: boolean;
  queryValue?: string;
  rows?: number;
  inputRows?: number;
  updateQuery?: string;
  arrow?: 'hidden' | 'block' | string;
  isCopied?: boolean;
  resCopiedIndex?: number | null;
  newResponseLoader?: boolean;
  chatHistory: DataItem[];
  showAnswer?: boolean;
  quesIndex?: number | null;
  token?: CancelTokenSource | null;
  useDocumentOnly?: boolean | undefined;
  chatHasDocument?: boolean | (() => boolean | void) | undefined;
  openMultipleFilesConfirmationModal?: boolean;
  uploadDocument?: boolean;
  webSearchEnabled?: boolean;
  setWebSearchEnabled?: (value: boolean) => void;
  thinkingEnabled?: boolean;
}

export interface momInitialStateProps {
  rag?: boolean | (() => boolean | void) | null | string;
  queryIndex?: number | null;
  responseIndex?: number | null;
  showQueryActions?: boolean;
  showResponseActions?: boolean;
  showEditQueryActions?: boolean;
  queryValue?: string;
  rows?: number;
  inputRows?: number;
  updateQuery?: string;
  arrow?: 'hidden' | 'block' | string;
  isCopied?: boolean;
  resCopiedIndex?: number | null;
  newResponseLoader?: boolean;
  momHistory: DataItem[];
  showAnswer?: boolean;
  quesIndex?: number | null;
  token?: CancelTokenSource | null;
  useDocumentOnly?: boolean | (() => boolean) | undefined;
  chatHasDocument?: boolean | (() => boolean | void) | undefined;
  openMultipleFilesConfirmationModal?: boolean;
  uploadDocument?: boolean;
}

export interface budgetStateProps {
  budgetModalOpen?: boolean;
}

export interface forgotPassProps {
  userdata?: { email?: string };
  errorInfo?: { email?: string };
  captchaToken?: string | null;
  error?: boolean;
  isCaptchaCompleted?: boolean;
  loading?: boolean;
}
export interface PermissionTypes {
  data?: string[]; // Array of strings representing actions like "create", "update", etc.
  id?: number[]; // Array of numbers representing IDs
  module?: string; // A string representing the module name
}
export interface configurationsTypes {
  embeddingModels: NestedRecord[];
  input_tokens_consumed: number;
  models: NestedRecord[];
  output_tokens_consumed: number;
  tokens_alloted: number;
  updated_at: string;
}
export interface userDetailsTypes {
  name: string;
  role: string;
  roleId: number;
  team: string;
  teamId: number;
  account_id?: number;
}
export interface PlayModelCardsProps {
  providerName?: string;
  modelName?: string;
  selectedImage?: string;
}
